/**
 * iter233 — Assurance du dossier, sélectionnable dès l'ouverture de l'OR.
 *
 * Dès que le tarif « Assurance » est choisi, on rattache la compagnie au dossier
 * ici (plus besoin d'attendre la facturation). L'assurance devient le
 * destinataire des documents (devis / facture / e-mails) sans jamais modifier le
 * client ni le véhicule réel de l'OR. Le N° de sinistre se saisit dans les
 * en-têtes du dossier (OrReferencesBlock).
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, Search, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function OrAssuranceCard({ or, onUpdated }) {
  const [picker, setPicker] = useState({ open: false, q: "", results: [], loading: false });
  const [busy, setBusy] = useState(false);
  const assurance = or?.assurance_detail;

  useEffect(() => {
    if (!picker.open) return;
    const q = (picker.q || "").trim();
    if (q.length < 2) { setPicker((p) => ({ ...p, results: [], loading: false })); return; }
    let alive = true;
    setPicker((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { q, type: "assurance", limit: 15 } });
        if (!alive) return;
        setPicker((p) => ({ ...p, results: data?.items || data || [], loading: false }));
      } catch {
        if (alive) setPicker((p) => ({ ...p, loading: false }));
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.q, picker.open]);

  const link = async (assuranceId) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/or/${or.id}`, { assurance_id: assuranceId });
      onUpdated?.(data);
      setPicker({ open: false, q: "", results: [], loading: false });
      toast.success(assuranceId ? "Assurance rattachée au dossier" : "Assurance retirée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-2.5"
      data-testid="or-assurance-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Assurance du dossier
          </div>
          {assurance ? (
            <div className="mt-1 text-sm min-w-0">
              <div className="font-semibold text-gray-900 truncate" data-testid="or-assurance-nom">
                {assurance.raison_sociale}
              </div>
              <div className="text-xs text-gray-600 truncate">
                {[assurance.localite, assurance.email_factures || assurance.email].filter(Boolean).join(" · ")}
              </div>
              <div className="text-[11px] text-violet-700 mt-1">
                La facture est établie au nom du client, puis envoyée à cette assurance (e-mail et courrier) avec le N° de sinistre. Le client et le véhicule de l&apos;OR ne changent pas.
              </div>
            </div>
          ) : (
            <div className="mt-1.5">
              <Button
                size="sm" variant="outline" disabled={busy}
                onClick={() => setPicker({ open: true, q: "", results: [], loading: false })}
                className="border-violet-300 text-violet-700 hover:bg-violet-100 h-7 text-xs"
                data-testid="or-assurance-link-btn"
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Sélectionner l&apos;assurance
              </Button>
            </div>
          )}
        </div>
        {assurance && (
          <div className="flex flex-col gap-1 shrink-0">
            <Button
              size="sm" variant="ghost" disabled={busy}
              className="h-7 text-xs text-violet-700"
              onClick={() => setPicker({ open: true, q: "", results: [], loading: false })}
              data-testid="or-assurance-change"
            >
              Changer
            </Button>
            <Button
              size="sm" variant="ghost" disabled={busy}
              className="h-7 text-xs text-gray-500"
              onClick={() => link(null)}
              data-testid="or-assurance-remove"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Retirer
            </Button>
          </div>
        )}
      </div>

      <Dialog open={picker.open} onOpenChange={(o) => !o && setPicker({ open: false, q: "", results: [], loading: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assurance du dossier</DialogTitle>
            <DialogDescription>
              Les documents de cet OR seront adressés à l&apos;assurance choisie, sans modifier le client / véhicule réel.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={picker.q}
              onChange={(e) => setPicker((p) => ({ ...p, q: e.target.value }))}
              placeholder="Rechercher une assurance (Generali, AXA, Zurich...)"
              className="pl-9"
              autoFocus
              data-testid="or-assurance-search"
            />
          </div>
          <div className="max-h-72 overflow-y-auto border rounded-lg divide-y divide-gray-100">
            {picker.loading && (
              <div className="p-3 text-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Recherche...
              </div>
            )}
            {!picker.loading && picker.q.trim().length < 2 && (
              <div className="p-4 text-center text-xs text-gray-400">Tape au moins 2 caractères pour rechercher.</div>
            )}
            {!picker.loading && picker.q.trim().length >= 2 && picker.results.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-500">Aucune assurance trouvée.</div>
            )}
            {picker.results.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => link(c.id)}
                disabled={busy}
                className="w-full text-left p-3 hover:bg-violet-50 transition-colors"
                data-testid={`or-assurance-option-${c.id}`}
              >
                <div className="font-semibold text-sm text-gray-900">{c.raison_sociale}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  {[c.localite, c.email].filter(Boolean).join(" · ")}
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicker({ open: false, q: "", results: [], loading: false })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
